import { FormEvent, useState } from "react";
import { Trash2 } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { IconButton } from "@/components/ui/IconButton";
import { useApiMutation } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { CollectionCell } from "@/types/billing";

interface Props {
  location: string;
  cell: CollectionCell;
  onChanged: () => void;
}

export function SettlementPanel({ location, cell, onChanged }: Props) {
  const exists = cell.status === "collected";
  const [start, setStart] = useState(cell.period_start ?? "");
  const [end, setEnd] = useState(cell.period_end ?? "");
  const [value, setValue] = useState("");
  const { mutate, loading } = useApiMutation();
  const { push } = useToast();

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await mutate("/api/billing/collection/settlement", "POST", {
        location, period_start: start, period_end: end, value,
      });
      push({ message: "Rozliczenie CO zapisane", tone: "success" });
      setValue("");
      onChanged();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  async function remove() {
    try {
      await mutate("/api/billing/collection/settlement", "DELETE", {
        location, period_start: cell.period_start, period_end: cell.period_end,
      });
      push({ message: "Rozliczenie usunięte", tone: "success" });
      onChanged();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  return (
    <Card padding="none">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h3 className="text-sm font-semibold capitalize">Rozliczenie CO · {location}</h3>
        <Badge tone={exists ? "success" : "muted"}>{exists ? "Zapisane" : "Brak"}</Badge>
      </div>

      {exists ? (
        <table className="w-full text-sm">
          <thead className="text-xs text-text-muted">
            <tr className="border-b border-border">
              <th className="text-left font-medium px-4 py-2">Okres</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Zebrano</th>
              <th className="px-4 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-2 text-xs font-mono">
                {cell.period_start} → {cell.period_end}
              </td>
              <td className="px-4 py-2">
                <Badge tone="success">Zebrano</Badge>
              </td>
              <td className="px-4 py-2 text-xs font-mono text-text-muted hidden sm:table-cell">
                {cell.collected_at ? cell.collected_at.slice(5, 10).replace("-", ".") : "—"}
              </td>
              <td className="px-4 py-2 text-right">
                <IconButton label="Usuń rozliczenie" onClick={remove}>
                  <Trash2 className="size-3.5" />
                </IconButton>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <form onSubmit={submit} className="px-4 py-4 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="sm:col-span-1">
              <Input id={`s-${location}`} label="Od (YYYY-MM)" placeholder="2025-07"
                     value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="sm:col-span-1">
              <Input id={`e-${location}`} label="Do (YYYY-MM)" placeholder="2026-04"
                     value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Input id={`v-${location}`} label="Wartość rozliczenia (PLN)" inputMode="decimal"
                     value={value} onChange={(e) => setValue(e.target.value)} />
            </div>
          </div>
          <div>
            <Button type="submit" loading={loading} disabled={!start || !end || !value}>
              Zapisz rozliczenie
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
